<template>
  <div class="field-cont">
    <label v-if="label && !is('check')">
      {{ label }}
      <span v-if="isRequired">
        *
      </span>
    </label>
    <div class="field" :class="classes">
      <icon v-if="_icon" :name="_icon"/>
      <span v-else-if="iconLabel" class="ic">
        {{ iconLabel }}
      </span>
      <input
        v-if="isInput"
        :autocomplete="autocomplete"
        :disabled="disabled"
        :min="_min"
        :max="_max"
        :name="name"
        :placeholder="placeholder"
        ref="input"
        :required="isRequired"
        :step="_step"
        :type="_type"
        :value="_value"
        @input="set($event)"/>
      <template v-else-if="is('check')">
        <div class="checkbox" :class="{ active: value }" @click="set(!value)">
          <icon name="check"/>
        </div>
        <label v-if="label" @click="set(!value)">
          {{ label }}
        </label>
      </template>
      <textarea
        v-else-if="is('textarea')"
        :placeholder="placeholder"
        :value="value"
        @input="set($event)"/>
      <select
        v-else-if="is('select')"
        :disabled="disabled"
        :placeholder="placeholder"
        :required="isRequired"
        :value="_value"
        @mousedown="setSelectIcon('up')"
        @mouseup="setSelectIcon('down')"
        @blur="setSelectIcon('down')"
        @focus="setFocus(true)"
        @focusout="setFocus(false);setSelectIcon('down')"
        @change="set($event)">
        <option
          v-for="opt in selectOptions"
          :key="`${opt[0]}`" :value="opt[0]">{{ opt[1] }}</option>
      </select>
      <slot/>
    </div>
  </div>
</template>

<script>
// import Cleave from 'cleave.js'
import {
  isArray, isNumeric, isPlainObject, toNumber, validate,
} from '../../utils'

const filters = {
  numeric: value => isNumeric(value) ? toNumber(value) : null,
  // TODO: generalize into a auto type casting function
  select: value => {
    if (value === null || value === '' || value === 'null') return null
    if (value === 'true') return true
    if (value === 'false') return false

    return isNaN(Number(value)) ? value : Number(value)
  },
  // money: value => isNumeric(value) ? Number(value) : null,
}

export default {
  props: {
    autocomplete: {},
    focus: {},
    label: {},
    type: {
      default: 'text',
    },
    placeholder: {},
    options: {},
    disabled: {
      default: false,
    },
    required: {
      default: false,
    },
    step: {},
    validation: {
      default: false,
    },
    min: {},
    max: {},
    name: {},
    info: {},
    icon: {},
    iconLabel: {},
    isIconRight: {},
    value: {},
    // cleaveOptions: {},
  },
  data() {
    return {
      selectIcon: 'down',
      isFocused: false,
      impliedType: this.type ? this.type : 'text',
      // cleave: null,
      // initialValue: null,
    }
  },
  computed: {
    el() {
      return this.$refs.input || this.$refs.textarea || this.$refs.select
    },
    // _cleaveOptions() {
    //   const options = Object.assign({}, this.cleaveOptions || {}, {
    //     time: {
    //       blocks: [2, 2],
    //       delimiter: ':',
    //       numericOnly: true,
    //       numeralPositiveOnly: true,
    //     },
    //   }[this.type] || {})

    //   if (!Object.keys(options).length) {
    //     return null
    //   }

    //   return options
    // },
    _icon() {
      return this.icon || {
        money: 'eur',
        time: 'clock',
        select: this._selectIcon,
      }[this.type]
    },
    _max() {
      return typeof this.max === 'undefined' ? {
        money: 1000000,
      }[this.type] : this.max
    },
    _min() {
      return typeof this.min === 'undefined' ? {
        money: 0,
      }[this.type] : this.min
    },
    _step() {
      return this.step || {
        money: 0.01,
        time: 1,
      }[this.type]
    },
    _type() {
      return {
        money: 'number',
        // time: 'text',
      }[this.type] || this.type
    },
    // _placeholder() {
    //   return this.placeholder || {
    //     time: 'hh:mm',
    //   }[this.type]
    // },
    _value() {
      return this.filterIn
        ? this.filterIn(this.value)
        : this.value
    },
    classes() {
      const conditions = [
        [this.disabled, 'disabled'],
        [this.isRequired, 'required'],
        [this.isFocused, 'focus'],
        [this.validClass, this.validClass],
        [this.is('select'), 'trans'],
        [this._icon || this.iconLabel, 'with-icon'],
        [this.isInput, 'inp'],
        [this._isIconRight, 'icon-right'],
      ]

      return conditions
        .filter(([condition]) => condition)
        .reduce((acc, [_, ...classes]) => [
          ...acc,
          ...classes,
        ], [`type-${this._type}`])
    },
    filterIn() {
      return {
        // time: filters.time.in,
      }[this.type]
    },
    filterOut() {
      const defaultHandler = value => value || null

      return {
        text: defaultHandler,
        email: defaultHandler,
        textarea: defaultHandler,
        number: filters.numeric,
        money: filters.numeric,
        select: filters.select,
        // time: filters.time,
      }[this.type]
    },
    _isIconRight() {
      return this.isIconRight || ['money', 'time'].includes(this.type)
    },
    isInput() {
      return ['email', 'number', 'password', 'text', 'time'].includes(this._type)
    },
    isRequired() {
      return this.required !== false
    },
    validationResult() {
      if (this.validation === false) {
        return null
      }

      if (typeof this.validation === 'function') {
        return this.validation(this.value)
      }

      return validate[this.validationType](this.value)
    },
    isValid() {
      return this.validationResult === true
    },
    _selectIcon() {
      return this.disabled
        ? ''
        : this.selectIcon
    },
    selectOptions() {
      let options = []

      if (isArray(this.options)) {
        if (this.options.length !== 0 && isArray(this.options[0])) {
          // array of pairs [key, value]
          options = this.options
        } else {
          // array of values
          options = this.options.map(option => [option, option])
        }
      } else if (isPlainObject(this.options)) {
        options = Object.keys(this.options).map(key => [key, this.options[key]])
      }

      // return array of pairs to ensure the order of elements
      return options
    },
    validClass() {
      if (this.validation === false || this.value === '' || this.value === null) {
        return ''
      }

      return this.isValid ? 'valid' : 'invalid'
    },
    validationType() {
      return this.validation === '' ? this.type : this.validation
    },
  },
  methods: {
    // emitChange() {
    //   console.log('value', this.value, this._value, this.cleave.getRawValue())
    //   // this.$emit('rawValueChanged', this.cleave.getRawValue())
    // },
    is(...types) {
      return types.some(type => this.impliedType === type)
    },
    set(value) {
      if (this.disabled) return

      if (typeof value === 'object') {
        value = value.target.value
        // if (!value && !this.initialValue) {
        //   value = this.initialValue
        // }
      }

      // if (this.cleave) {
      //   value = this.cleave.getRawValue()
      // }

      if (this.filterOut) {
        value = this.filterOut(value)
      }

      this.$emit('update:value', value)
      this.$emit('change', value)

      if (this.isValid) {
        this.$emit('valid', value)
      }
    },
    setSelectIcon(val) {
      this.selectIcon = val
    },
    setFocus(val) {
      this.isFocused = val
    },
  },
  watch: {
    validationResult(validationResult) {
      this.$emit('validate', validationResult)
    },
  },
  mounted() {
    if (this.focus && this.$refs.input) this.$refs.input.focus()
  },
  // mounted() {
  //   if (!this._cleaveOptions) {
  //     return
  //   }

  //   this.initialValue = this.value
  //   const $el = this.$refs.input
  //   $el.value = this._value
  //   this.cleave = new Cleave($el, this._cleaveOptions)
  // },
  // beforeDestroy() {
  //   if (this.cleave) {
  //     this.cleave.destroy()
  //   }
  // },
}
</script>

<style lang="scss">
@import '~style/variables';

.field-cont label, label.field-label {
  @include trans;
  color: $c-text-lightish;
  font-weight: 600;
  font-size: inherit;
  display: block;
  line-height: $lh * 1.5;
  // margin: 0 0 .2857rem;
  position: relative;
  .info-pop {
    position: absolute;
    top: 0;
    right: 0;
  }
}

.field {
  margin-bottom: 1rem;
  line-height: $lh * 1.5;
  input, select, textarea, input[type="number"], &.with-icon {
    background: #fff;
    border: 1px solid $c-border;
    border-radius: $r;
    font-size: inherit;
    // line-height: inherit;
    outline: none;
    width: 100%;
    &.focus {
      border-color: $c-info;
      box-shadow: 0 0 2px $c-info;
    }
  }
  input[type="number"] {
    padding-right: 0;
  }
  input, select, option, textarea {
    @include trans;
    font-weight: 600;
    padding: 0.25rem 0.75rem;
    &.success {
      border-color: $c-success;
    }
    &.info {
      border-color: $c-info;
    }
    &:active, &:focus {
      border-color: $c-info;
      box-shadow: 0 0 2px $c-info;
    }
  }
  textarea {
    min-height: 187px;
  }

  > .checkbox {
    @include trans(0.1s);
    background: #fff;
    border: 1px solid $c-border;
    border-radius: $r;
    cursor: pointer;
    float: left;
    font-size: $h5;
    height: 2rem;
    // line-height: inherit;
    line-height: 1.65rem;
    padding: 0.25rem 0;
    text-align: center;
    width: 2rem;
    > .ic {
      @include trans(0.3s);
      color: #ddd;
      display: block;
      transform: scale(0.8);
    }
    label {
      display: inline-block;
      cursor: inherit;
    }
    &.active {
      background: $c-info;
      border-color: $c-info;
      > .ic {
        color: #fff !important;
        transform: scale(1);
      }
    }
  }

  &:hover {
    > .checkbox {
      > .ic {
        color: $c-text-lightish;
      }
    }
  }

  &.type-select {
    select {
      background: transparent;
    }
    label {
      line-height: 1rem;
      margin: 0.75rem 0 0;
      padding-left: 0.75rem;
    }
  }

  &.type-check {
    @include clearfix;
    > label {
      cursor: pointer;
      float: left;
      padding-left: 0.5rem;
    }
  }

  &.disabled {
    > .checkbox {
      color: $c-text-lightish;
      cursor: not-allowed;
      label {
        color: $c-text-mid;
      }
    }
  }

  &.with-icon {
    display: flex;
    // padding-left: 3rem;
    // padding-right: 0;
    position: relative;

    input, select {
      border: none;
      &:active, &:focus {
        border-color: transparent;
        box-shadow: none;
      }
    }

    > .ic {
      border-right: 1px solid $c-border-light;
      font-size: $h4;
      font-weight: 600;
      // position: absolute;
      line-height: inherit;
      // top: 0; left: 0;
      padding: 0.33rem 0 0.25rem 1rem;
    }

    // modifiers
    &.trans {
      > .ic {
        border: none;
        background: transparent;
      }
    }

    &.icon-right {
      // padding-left: 0;
      // padding-right: 2.75rem;
      direction: rtl;
      > .ic {
        border-right: 0;
        left: auto;
        right: 0;
      }
    }

    &.type-select {
      padding-left: 0;
      padding-right: 0;
      > .ic {
        left: auto;
        padding: 0.375rem 0.5rem 0.25rem;
        pointer-events: none;
        position: absolute;
        right: 0;
      }
      select {
        padding-right: 1.5rem;
      }
    }

    // styles
    &.clean {
      padding-left: 1.25rem;
      .ic {
        background: transparent;
        border: none;
        padding: 0.25rem 0.5rem;
      }
    }
  }

  // state
  &.disabled {
    input {
      background: #eee;
    }
    &.with-icon {
      background: #eee;
    }
  }
  &.valid {
    input, .with-icon {
      border-color: $c-success;
      &:focus {
        box-shadow: $s-lg-success;
      }
    }
  }
  &.invalid {
    input, &.with-icon {
      border-color: $c-danger;
      &:focus {
        box-shadow: $s-lg-danger;
      }
    }
  }
}
.field.required label > span, label span.required {
  color: $c-danger;
}

.field-side {
  padding-top: 1.5rem;
  line-height: 3rem;
}

.field-group {
  border-bottom: 1px solid $c-border;
  padding: 1rem 0;
  > h4 {
    margin-bottom: 1rem;
  }
  &:first-of-type {
    padding-top: 0;
  }
  &:last-of-type {
    border-bottom: 0;
    padding-bottom: 0;
  }
}

.field-cont {
  // external modifiers
  &.center {
    input {
      padding-left: 0;
      padding-right: 0;
      text-align: center;
    }
    &.with-icon.inp.icon-right {
      input {
        text-align: center;
      }
    }
  }

  // sizes
  &.sm {
    .field {
      font-size: $h6;
      line-height: $lh;
      &.with-icon.inp {
        // padding-left: 1.5rem;
        &.icon-right {
          // padding-left: 0;
          // padding-right: 1.5rem;
          input {
            text-align: right;
          }
        }
        .ic {
          font-size: $h7;
          padding: 0.25rem 0.5rem;
        }
      }
    }
  }

  &.xs {
    .field {
      font-size: $h65;
      line-height: $lh;
      > label {
        line-height: 1.5rem;
      }
      .checkbox {
        font-size: 0.6rem;
        height: 1.25rem;
        line-height: 1.25rem;
        padding: 0;
        width: 1.25rem;
      }
    }
  }
}

.field-cont.sm label, label.field-label.sm {
  line-height: $lh;
  margin-bottom: 0;
}

input[type=number] {
  -moz-appearance: textfield;
}
</style>



// WEBPACK FOOTER //
// src/views/components/globals/Field.vue